import type { TaskDetail, Version } from '@mybusinessmate/schema';

const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

/**
 * What the client asked for when they sent a version back (Figma A05.4e): the round they are on, their words, and what
 * the worker does next. Shown only while the task is waiting on that fix, so it is gone once a new version goes up.
 */
export function ClientFeedbackBanner({ task, versions }: { task: TaskDetail; versions: Version[] }) {
  if (task.status !== 'Revision Requested') return null;
  const asked = versions.find((v) => v.clientFeedback);
  if (!asked?.clientFeedback) return null;

  const next = Math.max(...versions.map((v) => v.versionNumber)) + 1;
  const lead = task.team.find((m) => m.staffRole === 'lead_specialist')?.name.split(' ')[0];
  const last = task.revisionCount >= task.maxRevisions;
  const when = asked.clientReviewedAt ? new Date(asked.clientReviewedAt) : null;

  return (
    <section
      aria-label="What the client asked for"
      className="flex flex-col gap-(--mbm-space-1-5) rounded-(--mbm-component-radius-card) bg-feedback-warning-bg p-(--mbm-space-4-5)"
    >
      <p className="text-label-tag uppercase text-tag-amber-text">
        Client · revision {task.revisionCount} of {task.maxRevisions}
        {last ? ' · last one on their plan' : ''}
      </p>
      <p className="whitespace-pre-wrap text-heading-h4 text-text-primary">“{asked.clientFeedback}”</p>
      <p className="text-body-xsmall text-text-secondary">
        {[
          'The client',
          when ? `${day.format(when)}, ${time.format(when)}` : null,
          `fix it in v${next}${lead ? `, then send to ${lead} for QA` : ''}`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
    </section>
  );
}
